package com.dignicate.web.domain

/**
 * Represents a single localized text with its language code.
 * Used for content that will be managed by CMS in the future.
 * @param lang Language code as a `Language` sealed class
 * @param text The localized text content
 */
data class LocalizedText(
    val lang: Language,
    val text: String,
)

/**
 * Container for multiple localized texts.
 * Provides resolution logic to retrieve the appropriate text based on locale preferences.
 * @param values List of localized texts for different languages
 */
data class LocalizedTexts(
    val values: List<LocalizedText>
) {
    /**
     * Resolves the text for the preferred language with fallback support.
     * Accepts Language objects.
     * @param preferredLang The desired language
     * @param fallbackLang The fallback language (default: Language.En)
     * @return The resolved text, or empty string if no match found
     */
    fun resolve(preferredLang: Language, fallbackLang: Language = Language.En): String {
        // Try to find preferred language
        values.firstOrNull { it.lang.toString() == preferredLang.toString() }?.let { return it.text }

        // Try fallback language
        values.firstOrNull { it.lang.toString() == fallbackLang.toString() }?.let { return it.text }

        // Return first available or empty string
        return values.firstOrNull()?.text ?: ""
    }

    /**
     * Backwards-compatible overload: accepts language codes as strings.
     */
    fun resolve(preferredLang: String, fallbackLang: String = "en"): String {
        return resolve(Language.fromCode(preferredLang), Language.fromCode(fallbackLang))
    }

    companion object {
        /**
         * Preferred helper to create LocalizedTexts from vararg pairs of Language to text.
         * Example: LocalizedTexts.of(Language.En to "Hello", Language.Ja to "こんにちは")
         */
        fun of(vararg pairs: Pair<Language, String>): LocalizedTexts {
            return LocalizedTexts(pairs.map {
                LocalizedText(
                    it.first,
                    it.second
                )
            })
        }
    }
}
